import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../services/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import {
  formatDateTimeDisplay,
  formatTimeDisplay,
  getBrowserTimezone,
  resolveDisplayTimezone,
} from '../lib/datetime';
import {
  MessageSquare, X, Send, Loader2, Bot, User, AlertCircle,
  Minimize2, Maximize2, HeadphonesIcon, Sparkles, Bell, TrendingUp, TrendingDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

const DEFAULT_CHAT_TIMEZONE = getBrowserTimezone();

const timeframeToMinutes = (value) => {
  const normalized = String(value || '5m').trim().toLowerCase();
  if (normalized.endsWith('m')) return Number.parseInt(normalized, 10) || 5;
  if (normalized.endsWith('h')) return (Number.parseInt(normalized, 10) || 1) * 60;
  return Number.parseInt(normalized, 10) || 5;
};

const formatAlertDisplayTimestamp = (alert, fallbackTimezone) => {
  if (alert?.display_timestamp) {
    return alert.display_timestamp;
  }

  if (!alert?.timestamp) {
    return '';
  }

  const parsed = new Date(alert.timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return String(alert.timestamp);
  }

  const timezone = resolveDisplayTimezone(alert?.display_timezone || fallbackTimezone || DEFAULT_CHAT_TIMEZONE);
  return formatDateTimeDisplay(parsed, timezone, {
    second: '2-digit',
  });
};

const classifyAlertCurrency = (alert) => {
  const ts = alert?.timestamp;
  if (!ts) return null;
  const parsed = new Date(ts);
  if (Number.isNaN(parsed.getTime())) return null;
  const ageMs = Date.now() - parsed.getTime();
  if (ageMs < 0) return '[CURRENT]';
  if (ageMs <= 90 * 1000) return '[CURRENT]';
  if (ageMs < 60 * 60 * 1000) {
    return `[HISTORICAL — ${Math.round(ageMs / 60000)}m ago]`;
  }
  return `[HISTORICAL — ${Math.round(ageMs / 3600000)}h ago]`;
};

const normalizeAlertMessageContent = (alert, fallbackTimezone) => {
  const displayTimestamp = formatAlertDisplayTimestamp(alert, fallbackTimezone);
  const fallbackMessage = `Strategy Alert: ${alert?.strategy_name || 'Unknown Strategy'} - ${alert?.action || 'UNKNOWN'}`;
  const baseMessage = alert?.formatted_message || fallbackMessage;
  const currencyTag = classifyAlertCurrency(alert);
  const prefixedMessage = currencyTag ? `${currencyTag} ${baseMessage}` : baseMessage;

  if (!displayTimestamp) {
    return prefixedMessage;
  }

  if (/_Time:\s*.*_/i.test(prefixedMessage)) {
    return prefixedMessage.replace(/_Time:\s*.*_/i, `_Time: ${displayTimestamp}_`);
  }

  return `${prefixedMessage}\n\n_Time: ${displayTimestamp}_`;
};

const formatMessageFooterTime = (message, fallbackTimezone) => {
  if (message?.role === 'alert' && message?.alertData) {
    const timestamp = message.alertData.timestamp || message.timestamp;
    const timezone = resolveDisplayTimezone(message.alertData.display_timezone || fallbackTimezone || DEFAULT_CHAT_TIMEZONE);
    const parsed = new Date(timestamp);
    if (!Number.isNaN(parsed.getTime())) {
      return formatTimeDisplay(parsed, timezone);
    }
  }

  const parsed = new Date(message?.timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return formatTimeDisplay(parsed, resolveDisplayTimezone(fallbackTimezone || DEFAULT_CHAT_TIMEZONE));
};

const formatSignalAge = (value) => {
  const ms = Number(value);
  if (!Number.isFinite(ms) || ms < 0) return null;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60000)}m`;
};

const readableSignalSource = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  return normalized.replaceAll('_', ' ');
};

const ChatWidget = () => {
  const { user, isAuthenticated } = useAuth();
  const appTimeZone = resolveDisplayTimezone(user?.timezone);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chatStatus, setChatStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [requiresHuman, setRequiresHuman] = useState(false);
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [escalateSubject, setEscalateSubject] = useState('');
  const [escalatePriority, setEscalatePriority] = useState('medium');
  const [isEscalated, setIsEscalated] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [strategyAlerts, setStrategyAlerts] = useState([]);
  const [hasUnreadAlerts, setHasUnreadAlerts] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const alertIntervalRef = useRef(null);
  const pushIntervalRef = useRef(null);
  const assistantWarningRef = useRef(false);

  const warnAssistantOnce = (message, error) => {
    if (assistantWarningRef.current) return;
    assistantWarningRef.current = true;
    console.warn(message, error?.message || error);
  };

  // Check if push notifications are supported
  useEffect(() => {
    setPushSupported('Notification' in window && 'serviceWorker' in navigator);
  }, []);

  // Check chat availability and start alert polling
  useEffect(() => {
    if (isAuthenticated) {
      checkChatStatus();
      checkStrategyAlerts();
      checkPushStatus();
      
      // Poll for strategy alerts every 30 seconds
      alertIntervalRef.current = setInterval(checkStrategyAlerts, 30000);
      
      // Poll for push notifications every 10 seconds
      pushIntervalRef.current = setInterval(checkPushNotifications, 10000);
      
      return () => {
        if (alertIntervalRef.current) clearInterval(alertIntervalRef.current);
        if (pushIntervalRef.current) clearInterval(pushIntervalRef.current);
      };
    }
  }, [isAuthenticated]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  useEffect(() => {
    const openAssistant = () => {
      setIsOpen(true);
      setIsMinimized(false);
      setHasUnreadAlerts(false);
    };
    window.addEventListener('tradopro:assistant:open', openAssistant);
    return () => window.removeEventListener('tradopro:assistant:open', openAssistant);
  }, []);

  const checkChatStatus = async () => {
    try {
      const res = await api.get('/chatbot/status');
      setChatStatus(res.data);
    } catch (error) {
      warnAssistantOnce('TradoPro Assistant unavailable: status check failed.', error);
      setChatStatus({
        available: false,
        ai_enabled: false,
        message: 'Assistant unavailable',
      });
    }
  };

  const checkStrategyAlerts = async () => {
    try {
      const alertSymbol = localStorage.getItem("tp_chart_symbol") || "ES";
      const alertTimeframe = localStorage.getItem("tp_chart_timeframe") || "5m";
      const alertStrategyRef = localStorage.getItem("tp_chart_strategy_ref") || "none";
      const alertAccountId = localStorage.getItem("tp_dashboard_account_id") || "";
      const params = {
        symbol: alertSymbol,
        interval_minutes: timeframeToMinutes(alertTimeframe),
      };
      if (alertStrategyRef && alertStrategyRef !== "none") {
        params.strategy_ref = alertStrategyRef;
      }
      if (alertAccountId) {
        params.trading_account_id = alertAccountId;
      }
      const res = await api.get('/strategies/alerts', {
        params,
      });
      if (res.data.alerts && res.data.alerts.length > 0) {
        const newAlerts = res.data.alerts;
        setStrategyAlerts(prev => [...prev, ...newAlerts].slice(-20)); // Keep last 20
        setHasUnreadAlerts(true);
        
        // Add alerts to chat as system messages
        newAlerts.forEach(alert => {
          const alertMessage = {
            role: 'alert',
            content: normalizeAlertMessageContent(
              alert,
              appTimeZone,
            ),
            timestamp: alert.timestamp,
            alertData: alert
          };
          setMessages(prev => [...prev, alertMessage]);
          
          // Show toast notification
          toast.info(`Signal Alert: ${alert.strategy_name}`, {
            description: `${alert.action} setup detected for ${alert.symbol}. This is a signal, not a broker fill.`,
            duration: 8000
          });
        });
        
        // Auto-open chat if there are strong alerts
        const strongAlerts = newAlerts.filter(a => a.strength === 'STRONG');
        if (strongAlerts.length > 0 && !isOpen) {
          setIsOpen(true);
        }
      }
    } catch (error) {
      // Silently fail - alerts are optional
      console.debug('No strategy alerts:', error.message);
    }
  };

  const checkPushStatus = async () => {
    try {
      const res = await api.get('/chatbot/push/status');
      setPushEnabled(res.data.subscribed);
    } catch (error) {
      console.debug('Push status check failed:', error.message);
    }
  };

  const checkPushNotifications = async () => {
    if (!pushEnabled) return;
    
    try {
      const res = await api.get('/chatbot/push/notifications');
      const notifications = res.data.notifications || [];
      const unreadNotifications = notifications.filter(n => !n.read);
      
      if (unreadNotifications.length > 0) {
        setHasUnreadAlerts(true);
        
        // Show browser notifications if permission granted
        if (Notification.permission === 'granted') {
          unreadNotifications.forEach(notif => {
            new Notification(notif.title, {
              body: notif.body,
              icon: '/favicon.ico',
              tag: notif.id
            });
          });
        }
        
        // Mark as read
        await api.get('/chatbot/push/notifications', { params: { mark_read: true } });
      }
    } catch (error) {
      console.debug('Push notification check failed:', error.message);
    }
  };

  const enablePushNotifications = async () => {
    if (!pushSupported) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }
    
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        toast.error('Push notification permission denied');
        return;
      }
      
      // Subscribe to push notifications
      await api.post('/chatbot/push/subscribe', {
        endpoint: 'browser-notification',
        keys: { browser: true }
      });
      
      setPushEnabled(true);
      toast.success('Push notifications enabled! You\'ll receive alerts even when the app is in the background.');
    } catch (error) {
      toast.error('Failed to enable push notifications');
    }
  };

  const disablePushNotifications = async () => {
    try {
      await api.delete('/chatbot/push/unsubscribe');
      setPushEnabled(false);
      toast.info('Push notifications disabled');
    } catch (error) {
      toast.error('Failed to disable push notifications');
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || loading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    
    setLoading(true);
    
    try {
      const res = await api.post('/chatbot/chat', {
        message: userMessage,
        conversation_id: conversationId
      });
      
      // Set conversation ID from response
      if (!conversationId) {
        setConversationId(res.data.conversation_id);
      }
      
      // Add AI response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.message,
        timestamp: new Date().toISOString()
      }]);
      
      // Check if human support is needed
      if (res.data.requires_human) {
        setRequiresHuman(true);
      }
      
      // Check if already escalated
      if (res.data.ticket_id) {
        setIsEscalated(true);
        setTicketId(res.data.ticket_id);
      }
      
    } catch (error) {
      const detail = getApiErrorMessage(error, 'Please upgrade to BASIC or higher to use the AI chatbot.');
      
      if (error.response?.status === 403) {
        // Subscription required
        setMessages(prev => [...prev, {
          role: 'system',
          content: detail || 'Please upgrade to BASIC or higher to use the AI chatbot.',
          timestamp: new Date().toISOString()
        }]);
      } else {
        warnAssistantOnce('TradoPro Assistant unavailable: message request failed.', error);
        toast.error('Assistant unavailable');
        setMessages(prev => [...prev, {
          role: 'system',
          content: 'Assistant unavailable. Please check the connection and try again.',
          timestamp: new Date().toISOString()
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (!escalateSubject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    
    try {
      const res = await api.post('/chatbot/escalate', {
        conversation_id: conversationId,
        subject: escalateSubject,
        priority: escalatePriority
      });
      
      setIsEscalated(true);
      setTicketId(res.data.ticket_id);
      setShowEscalateDialog(false);
      setRequiresHuman(false);
      
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Your request has been escalated to our support team. Ticket ID: ${res.data.ticket_id}. You'll receive a response via email within 24 hours.`,
        timestamp: new Date().toISOString()
      }]);
      
      toast.success('Request escalated to support team');
    } catch (error) {
      toast.error('Failed to escalate request');
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setConversationId(null);
    setIsEscalated(false);
    setTicketId(null);
    setRequiresHuman(false);
    setHasUnreadAlerts(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleOpenChat = () => {
    setIsOpen(true);
    setHasUnreadAlerts(false);
  };

  // Don't show for non-authenticated users
  if (!isAuthenticated) return null;

  return (
    <>
      {/* Chat Button with Alert Indicator */}
      {!isOpen && (
        <button
          onClick={handleOpenChat}
          aria-label="Open TradoPro assistant"
          className="fixed bottom-6 right-6 hidden w-14 h-14 lg:flex bg-blue-600 hover:bg-blue-700 rounded-full shadow-lg items-center justify-center transition-all hover:scale-105 z-50"
          data-testid="chat-widget-toggle"
        >
          <MessageSquare className="w-6 h-6 text-white" />
          {hasUnreadAlerts && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
              <Bell className="w-3 h-3 text-white" />
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed z-50 bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl transition-all flex flex-col ${
            isMinimized
              ? 'bottom-[calc(6.75rem+env(safe-area-inset-bottom))] right-4 w-[calc(100vw-2rem)] h-14 sm:w-72 lg:bottom-6 lg:right-6'
              : 'inset-x-3 bottom-[calc(6.75rem+env(safe-area-inset-bottom))] h-[min(68vh,540px)] sm:inset-x-auto sm:right-4 sm:w-96 lg:bottom-6 lg:right-6 lg:h-[500px]'
          }`}
          data-testid="chat-widget-window"
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 cursor-pointer flex-shrink-0"
            onClick={() => isMinimized && setIsMinimized(false)}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-white">TradoPro Assistant</h3>
                {!isMinimized && (
                  <p className="text-xs text-zinc-500">
                    {chatStatus?.ai_enabled ? 'AI Powered' : 'Support'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Push Notification Toggle */}
              {!isMinimized && pushSupported && (
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    pushEnabled ? disablePushNotifications() : enablePushNotifications();
                  }}
                  className={`p-1.5 rounded-lg transition-colors ${
                    pushEnabled 
                      ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30' 
                      : 'hover:bg-zinc-800 text-zinc-400'
                  }`}
                  title={pushEnabled ? 'Disable push notifications' : 'Enable push notifications'}
                >
                  <Bell className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
                aria-label={isMinimized ? "Expand assistant" : "Minimize assistant"}
                className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                {isMinimized ? (
                  <Maximize2 className="w-4 h-4 text-zinc-400" />
                ) : (
                  <Minimize2 className="w-4 h-4 text-zinc-400" />
                )}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                aria-label="Close assistant"
                className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>
          </div>

          {/* Chat Body */}
          {!isMinimized && (
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              {/* Messages */}
              <ScrollArea className="flex-1 min-h-0 p-4">
                {/* Welcome message */}
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <Bot className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                    <h4 className="font-semibold text-white mb-2">Hi there!</h4>
                    <p className="text-sm text-zinc-400 mb-4">
                      I'm your TradoPro assistant. Ask me about trading, strategies, or platform features!
                    </p>
                    {/* Push notification prompt */}
                    {pushSupported && !pushEnabled && (
                      <button
                        onClick={enablePushNotifications}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/10 border border-blue-500/30 rounded-lg text-sm text-blue-400 hover:bg-blue-600/20 transition-colors"
                      >
                        <Bell className="w-4 h-4" />
                        Enable push notifications for alerts
                      </button>
                    )}
                    {chatStatus && !chatStatus.available && (
                      <Badge variant="outline" className="mt-3 text-yellow-400 border-yellow-400/50">
                        Upgrade to unlock AI chat
                      </Badge>
                    )}
                  </div>
                )}

                {/* Message list */}
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-xl px-4 py-2.5 ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : msg.role === 'system'
                            ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-200'
                            : msg.role === 'alert'
                            ? 'bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30 text-green-200'
                            : 'bg-zinc-800 text-zinc-200'
                        }`}
                      >
                        {msg.role === 'assistant' && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <Bot className="w-3 h-3 text-blue-400" />
                            <span className="text-[10px] text-blue-400 uppercase font-medium">
                              {msg.is_human_agent ? 'Support Agent' : 'AI'}
                            </span>
                          </div>
                        )}
                        {msg.role === 'system' && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <AlertCircle className="w-3 h-3 text-yellow-400" />
                            <span className="text-[10px] text-yellow-400 uppercase font-medium">System</span>
                          </div>
                        )}
                        {msg.role === 'alert' && (() => {
                          const alertTs = msg.alertData?.timestamp || msg.timestamp;
                          const alertParsed = alertTs ? new Date(alertTs).getTime() : NaN;
                          const ageMs = Number.isFinite(alertParsed) ? Date.now() - alertParsed : null;
                          // Mark "historical" if older than 10 minutes — keeps a
                          // ~6-bar window of relevance for typical 1-2m strategies.
                          const isHistorical = ageMs !== null && ageMs > 10 * 60 * 1000;
                          const automationContext = msg.alertData?.automation_context || {};
                          const triggerSource = automationContext.trigger_source || msg.alertData?.trigger_source;
                          const isPersistentEntry = triggerSource === 'persistent_state_entry';
                          const signalMode = readableSignalSource(
                            automationContext.signal_mode || msg.alertData?.signal_mode || msg.alertData?.source,
                          );
                          const signalAge = formatSignalAge(
                            automationContext.signal_age_ms ?? msg.alertData?.signal_age_ms,
                          );
                          return (
                            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                              {msg.alertData?.action?.includes('BUY') || msg.alertData?.action?.includes('CLOSE_SHORT') ? (
                                <TrendingUp className={`w-3 h-3 ${isPersistentEntry ? 'text-amber-400' : 'text-green-400'}`} />
                              ) : (
                                <TrendingDown className={`w-3 h-3 ${isPersistentEntry ? 'text-amber-400' : 'text-red-400'}`} />
                              )}
                              {isPersistentEntry ? (
                                <span className="text-[10px] text-amber-400 uppercase font-medium">
                                  Persistent/Resume Entry
                                </span>
                              ) : (
                                <span className="text-[10px] text-green-400 uppercase font-medium">
                                  Strategy Signal
                                </span>
                              )}
                              {msg.alertData?.strength === 'STRONG' && (
                                <Badge className="ml-1 text-[8px] px-1 py-0 bg-red-500/20 text-red-400 border-red-500/30">
                                  STRONG
                                </Badge>
                              )}
                              {isPersistentEntry && (
                                <Badge
                                  className="ml-1 text-[8px] px-1 py-0 bg-amber-500/15 text-amber-300 border-amber-500/30 uppercase tracking-wider"
                                  title="This is a persistent/resume entry — current regime still valid when auto-trade resumed. Not a fresh crossover signal."
                                >
                                  Regime Valid
                                </Badge>
                              )}
                              {isHistorical && (
                                <Badge
                                  className="ml-1 text-[8px] px-1 py-0 bg-zinc-700/40 text-zinc-300 border-zinc-500/40 uppercase tracking-wider"
                                  title="This alert is older than 10 minutes — verify against current chart state."
                                >
                                  Historical
                                </Badge>
                              )}
                              {msg.alertData?.automation_context?.decision && (
                                <Badge
                                  className="ml-1 text-[8px] px-1 py-0 bg-blue-500/10 text-blue-300 border-blue-500/30 uppercase tracking-wider"
                                  title={automationContext.reason_detail || automationContext.stale_reason || 'Latest automation decision for this alert scope.'}
                                >
                                  Auto: {String(automationContext.decision).replaceAll('_', ' ')}
                                </Badge>
                              )}
                              {signalMode && !isPersistentEntry && (
                                <Badge
                                  className="ml-1 text-[8px] px-1 py-0 bg-zinc-800/70 text-zinc-300 border-zinc-600/50 uppercase tracking-wider"
                                  title="Signal source. Strategy alerts are read-only unless the automation decision confirms an execution job."
                                >
                                  {signalMode}
                                </Badge>
                              )}
                              {signalAge && (
                                <Badge
                                  className="ml-1 text-[8px] px-1 py-0 bg-zinc-800/70 text-zinc-300 border-zinc-600/50 uppercase tracking-wider"
                                  title="Signal age when the automation decision was recorded."
                                >
                                  Age {signalAge}
                                </Badge>
                              )}
                            </div>
                          );
                        })()}
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.role === 'alert' && msg.alertData?.automation_context && (() => {
                          const automationContext = msg.alertData.automation_context;
                          const isPersistentEntry = (automationContext.trigger_source || msg.alertData?.trigger_source) === 'persistent_state_entry';
                          return (
                            <p className="text-[10px] mt-2 text-zinc-400">
                              {isPersistentEntry
                                ? 'Persistent/resume entry — current regime still valid at time of evaluation. Not a fresh crossover signal.'
                                : 'Signal role: automation candidate, not a broker fill.'}
                              {automationContext.reason_code && (
                                <> Reason: {String(automationContext.reason_code).replaceAll('_', ' ')}.</>
                              )}
                            </p>
                          );
                        })()}
                        <p className="text-[10px] opacity-50 mt-1">
                          {formatMessageFooterTime(
                            msg,
                            appTimeZone,
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Loading indicator */}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-zinc-800 rounded-xl px-4 py-3">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Escalate Banner */}
              {requiresHuman && !isEscalated && (
                <div className="px-4 py-2 bg-yellow-500/10 border-t border-yellow-500/30">
                  <button
                    onClick={() => setShowEscalateDialog(true)}
                    className="flex items-center gap-2 text-yellow-400 text-sm hover:text-yellow-300 transition-colors"
                  >
                    <HeadphonesIcon className="w-4 h-4" />
                    Talk to a human agent
                  </button>
                </div>
              )}

              {/* Escalated Banner */}
              {isEscalated && (
                <div className="px-4 py-2 bg-green-500/10 border-t border-green-500/30">
                  <div className="flex items-center justify-between">
                    <span className="text-green-400 text-xs">
                      Ticket #{ticketId?.slice(0, 8)} - Awaiting response
                    </span>
                    <button
                      onClick={startNewConversation}
                      className="text-xs text-zinc-400 hover:text-white"
                    >
                      New conversation
                    </button>
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="p-4 border-t border-zinc-800 bg-zinc-900/95 backdrop-blur rounded-b-xl flex-shrink-0">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isEscalated ? "Waiting for support..." : "Type a message..."}
                    disabled={loading || isEscalated}
                    className="flex-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 h-10"
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!inputValue.trim() || loading || isEscalated}
                    className="bg-blue-600 hover:bg-blue-700 h-10 w-10 p-0"
                    data-testid="chat-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Escalate Dialog */}
      <Dialog open={showEscalateDialog} onOpenChange={setShowEscalateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HeadphonesIcon className="w-5 h-5 text-blue-500" />
              Talk to Support
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Subject</label>
              <Input
                value={escalateSubject}
                onChange={(e) => setEscalateSubject(e.target.value)}
                placeholder="Brief description of your issue"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Priority</label>
              <div className="flex gap-2">
                {['low', 'medium', 'high'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setEscalatePriority(p)}
                    className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${
                      escalatePriority === p
                        ? p === 'high'
                          ? 'bg-red-600 text-white'
                          : p === 'medium'
                          ? 'bg-yellow-600 text-white'
                          : 'bg-green-600 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-zinc-500">
              Your conversation history will be shared with our support team. You'll receive a response via email within 24 hours.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEscalate} className="bg-blue-600 hover:bg-blue-700">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatWidget;
